import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Divider,
    Autocomplete, Paper, Snackbar, Alert, Modal, LinearProgress,
    IconButton
} from '@mui/material';
import { toast } from 'react-toastify';
import { Add, Delete } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { axiosInstance, localAxiosPut } from '../../../../Axios/axios';
import ScrollToTop from '../../../../components/ScrollToTop';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';
import { el } from 'date-fns/locale';

// --- Static Data for Dropdowns ---
const genderOptions = ['Male', 'Female', 'Other', 'Not to specify'];
const maritalStatusOptions = ['Single', 'Married', 'Divorced', 'Widowed'];
const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const emergencyRelationOptions = ['Parent', 'Spouse', 'Sibling', 'Friend', 'Other'];

// Data for Indian States and Union Territories (for Autocomplete)
const indianStatesAndUTs = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
    "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

// --- Utility for Subheadings ---
const SubHeading = ({ children }) => (
    <Typography
        variant="h6"
        sx={{
            mt: 3,
            mb: 2,
            pb: 0.5,
            borderBottom: '2px solid #ddd',
            fontWeight: 700,
        }}
    >
        {children}
    </Typography>
);

const PersonalContactDetails = ({
    userId,
    initialData,
    navigationContext,
    onSaveAndReturn,
    onUpdateEmployeeSuccess,
    onFormCompletionChange,
}) => {

    // --- Initial Form State ---
    const [formData, setFormData] = useState({
        employeeId: '',
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        maritalStatus: '',
        bloodGroup: '',
        primaryPhone: '',
        personalEmail: '',
        workEmail: '',
        permanentAddressLine1: '',
          permanentAddressLine2: '',
        permanentState: '',
        permanentCity: '',
        permanentPincode: '',
        permanentCountry: 'India',
        currentAddressLine1: '',
        currentAddressLine2: '',
        currentState: '',
        currentCity: '',
        currentPincode: '',
        currentCountry: 'India',
        // emergencyContactName: '',
        // emergencyContactRelation: '',
        // emergencyContactPhone: '',

        emergencyContacts: [{
            name: '',
            relation: '',
            phone: ''
        }
        ],
    });

    const [isCurrentSameAsPermanent, setIsCurrentSameAsPermanent] = useState(false);
    const accessToken = localStorage.getItem('accessToken');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate();


    // Calculate form completion progress
    const calculateProgress = () => {
        const allFields = [
            // Personal Info
            'employeeId',
            'firstName',
            'lastName',
            'dateOfBirth',
            'gender',
            'maritalStatus',
            'bloodGroup',
            'primaryPhone',
            'personalEmail',
            'workEmail',

            // Permanent Address
            'permanentAddressLine1',
             'permanentAddressLine2',
            'permanentState',
            'permanentCity',
            'permanentPincode',
            'permanentCountry',

            // Current Address (if not same)
            ...(isCurrentSameAsPermanent ? [] : [
                'currentAddressLine1',
                'currentAddressLine2',
                'currentState',
                'currentCity',
                'currentPincode',
                'currentCountry'
            ]),

            // Emergency Contact
            'emergencyContacts.name',
            'emergencyContacts.relation',
            'emergencyContacts.phone'
        ];

        const filledCount = allFields.filter(
            field => formData[field]?.toString().trim() !== ''
        ).length;

        // ✅ Return completion percentage
        return Math.round((filledCount / allFields.length) * 100);
    };

    // ✅ Use it like this:
    const completion = calculateProgress();



    const handleCloseTokenExpirePopup = () => {
        setOpenTokenExpirePopup(false);
        navigate('/');
    };

    // 3. Effect to Load Initial Data
    useEffect(() => {
        if (initialData) {
            const data = initialData.user || initialData; // Handle both nested and direct data

            // Extract addresses
            const permanentAddress = data.addresses?.find(addr => addr.address_type === 'permanent') || {};
            const currentAddress = data.addresses?.find(addr => addr.address_type === 'current') || {};

            // Extract emergency contact
            const loadedEmergencyContacts = data.emergency_contacts;

            const initialEmergencyContacts = loadedEmergencyContacts.length > 0 ?
                loadedEmergencyContacts.map(contact => ({
                    name: contact.contact_name || '',
                    relation: contact.relationship || '',
                    phone: contact.contact_number || '',
                })) :
                // Default to one empty contact if the array is empty/null
                [{ name: '', relation: '', phone: '' }];


            const loadedData = {
                employeeId: data.emp_id || '',
                firstName: data.first_name || '',
                lastName: data.last_name || '',
                dateOfBirth: data.dob ? data.dob.split('T')[0] : '',
                gender: data.gender || '',
                maritalStatus: data.marital_status || '',
                bloodGroup: data.blood_group || '',

                primaryPhone: data.phone_number || '',
                personalEmail: data.personal_email || '',
                workEmail: data.email || '',

                // Permanent Address
                permanentAddressLine1: permanentAddress.address_line || '',
                 permanentAddressLine2: permanentAddress.address_line2 || '',
                permanentState: permanentAddress.state || '',
                permanentCity: permanentAddress.city || '',
                permanentPincode: permanentAddress.pincode || '',
                permanentCountry: permanentAddress.country || 'India',

                // Current Address
                currentAddressLine1: currentAddress.address_line || '',
                currentAddressLine2: currentAddress.address_line2 || '',
                currentState: currentAddress.state || '',
                currentCity: currentAddress.city || '',
                currentPincode: currentAddress.pincode || '',
                currentCountry: currentAddress.country || 'India',

                // Emergency Contact
                emergencyContacts: initialEmergencyContacts
            };

            setFormData(loadedData);

            // Check if addresses are the same
            const areAddressesSame = (
                loadedData.permanentAddressLine1 === loadedData.currentAddressLine1 &&
                loadedData.permanentAddressLine2 === loadedData.currentAddressLine2 &&
                loadedData.permanentState === loadedData.currentState &&
                loadedData.permanentCity === loadedData.currentCity &&
                loadedData.permanentPincode === loadedData.currentPincode &&
                !!loadedData.permanentAddressLine1
            );
            setIsCurrentSameAsPermanent(areAddressesSame);
        }
        onFormCompletionChange && onFormCompletionChange(completion);

    }, [initialData, userId]);

    // 4. Handle standard input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prevData => {
            let newFormData = { ...prevData, [name]: value };

            if (isCurrentSameAsPermanent && name.startsWith('permanent')) {
                const currentKey = 'current' + name.substring('permanent'.length);
                newFormData[currentKey] = value;
            }
            return newFormData;
        });
    };

    // 5. Handle Autocomplete (State) Change
    const handleStateChange = (fieldPrefix) => (event, newValue) => {
        const name = `${fieldPrefix}State`;

        setFormData(prevData => {
            let newFormData = { ...prevData, [name]: newValue || '' };

            if (isCurrentSameAsPermanent && fieldPrefix === 'permanent') {
                newFormData.currentState = newValue || '';
            }
            return newFormData;
        });
    };

    // 6. Handle Checkbox Change
    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;
        setIsCurrentSameAsPermanent(checked);

        if (checked) {
            setFormData(prevData => ({
                ...prevData,
                currentAddressLine1: prevData.permanentAddressLine1,
                currentAddressLine2: prevData.permanentAddressLine2,
                currentState: prevData.permanentState,
                currentCity: prevData.permanentCity,
                currentPincode: prevData.permanentPincode,
                currentCountry: prevData.permanentCountry,
            }));
        }
    };

    // 7. Validation Logic
    // Replace your validateForm function with this one that shows what's missing
    const validateForm = () => {
        const errors = [];

        if (!formData.firstName) errors.push('First Name');
        if (!formData.lastName) errors.push('Last Name');
        if (!formData.dateOfBirth) errors.push('Date of Birth');
        if (!formData.gender) errors.push('Gender');

        if (!formData.primaryPhone) {
            errors.push('Contact Number');
        } else if (!/^(\+\d{1,3}[- ]?)?\d{10}$/.test(formData.primaryPhone)) {
            errors.push('Contact Number (must be 10 digits)');
        }



        if (!formData.permanentAddressLine1) errors.push('Permanent Address Line 1');
        if (!formData.permanentState) errors.push('Permanent State');
        if (!formData.permanentPincode) errors.push('Permanent Pincode');

        if (formData.permanentPincode && !/^\d{6}$/.test(formData.permanentPincode)) {
            errors.push('Permanent Pincode (must be 6 digits)');
        }

        if (!isCurrentSameAsPermanent) {
            if (!formData.currentAddressLine1) errors.push('Current Address Line 1');
            if (!formData.currentState) errors.push('Current State');
            if (!formData.currentPincode) errors.push('Current Pincode');
        }

        if (errors.length > 0) {
            toast.error(`Missing/Invalid fields: ${errors.join(', ')}`);
            return false;
        }

        return true;
    };

    // 8. Handle form submission (Save Changes)
    const performSave = async () => {

        if (!validateForm()) {
            setSnackbar({
                open: true,
                message: 'Please fill all the mandatory fields',
                severity: 'error'
            })
            return false;
        }

        const payload = {
            userFields: {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.workEmail,
                phone_number: formData.primaryPhone,
                dob: formData.dateOfBirth || null,
                emp_id: formData.employeeId || initialData.user?.emp_id || '',
            },
            personalDetails: {
                gender: formData.gender,
                blood_group: formData.bloodGroup || null,
                employment_type_id: initialData.user?.employment_type_id || 1,
                marital_status: formData.maritalStatus || null,
                personal_email: formData.personalEmail || null,
            },
            addresses: [
                // Current Address
                {
                    address_type: "current",
                    address_line: isCurrentSameAsPermanent ? formData.permanentAddressLine1 : formData.currentAddressLine1,
                    address_line2: isCurrentSameAsPermanent ? formData.permanentAddressLine2: formData.currentAddressLine2 || '',
                    city: isCurrentSameAsPermanent ? formData.permanentCity : formData.currentCity,
                    state: isCurrentSameAsPermanent ? formData.permanentState : formData.currentState,
                    pincode: isCurrentSameAsPermanent ? formData.permanentPincode : formData.currentPincode,
                    country: isCurrentSameAsPermanent ? formData.permanentCountry : formData.currentCountry
                },
                // Permanent Address
                {
                    address_type: "permanent",
                    address_line: formData.permanentAddressLine1,
                      address_line2: formData.permanentAddressLine2 || '',
                    city: formData.permanentCity,
                    state: formData.permanentState,
                    pincode: formData.permanentPincode,
                    country: formData.permanentCountry
                }
            ],
            emergencyContacts: formData.emergencyContacts.map((contact) => ({
                contact_name: contact.name,
                relationship: contact.relation,
                contact_number: contact.phone
            }))
        };

        console.log('payload for contacts', payload);

        try {
            const response = await axiosInstance(localAxiosPut(`/hr/updatePersonalContactTab/${userId}`, payload, accessToken));
            if (response.data.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Updated successfully',
                    severity: 'success'
                });
                onUpdateEmployeeSuccess();

                return { success: true, verificationStep: navigationContext?.verificationStep };
            }


        } catch (error) {
            console.error('API Save Error:', error);

            const apiError = error?.response?.data;
            let userFriendlyMessage = 'An unexpected error occurred.';

            if (apiError?.message?.error === 'Validation error') {
                userFriendlyMessage = 'Please check your fields';
            } else if (apiError?.message?.msg) {
                userFriendlyMessage = apiError.message.msg;
            } else if (error.message?.includes('Network Error')) {
                userFriendlyMessage = 'Network error — please check your connection.';
            }

            setSnackbar({
                open: true,
                message: userFriendlyMessage,
                severity: 'error'
            });
        }
    };

    // Update handleSaveChanges to use the shared function
    const handleSaveChanges = async (e) => {
        e.preventDefault();

        const success = await performSave();


        if (navigationContext?.returnToRehire && onSaveAndReturn) {
            onSaveAndReturn();
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleAnotherEmergencyContact = () => {
        setFormData(prevData => ({
            ...prevData,
            emergencyContacts: [...prevData.emergencyContacts, { name: '', relation: '', phone: '' }]
        }));
    };

    const handleEmergencyContactChange = (index, e) => {
        const { name, value } = e.target;
        setFormData(prevData => {
            const updatedContacts = prevData.emergencyContacts.map((contact, i) => {
                if (i === index) { // For now, only updating the first contact
                    return { ...contact, [name]: value };
                }
                return contact;
            });
            return { ...prevData, emergencyContacts: updatedContacts };
        });
    };
    const handleRemoveEmergencyContact = (indexToRemove) => {
        setFormData(prevData => ({
            ...prevData,
            emergencyContacts: prevData.emergencyContacts.filter(
                (_, index) => index !== indexToRemove
            ),
        }));
    };


    return (
        <Box component="form" onSubmit={handleSaveChanges} sx={{ p: 1 }}>
            <Grid container spacing={3}>

                {/* Section 1: Personal & Primary Contact Details */}
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <SubHeading>Personal & Primary Contact Details</SubHeading>

                        {/* Form Completion Progress Bar */}
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {`Form Completion: ${completion}%`}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={completion}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    mt: 0.5,
                                    backgroundColor: '#e0e0e0',
                                    width: '100%',
                                    transition: 'all 0.5s ease'
                                }}
                                color={completion === 100 ? 'success' : 'primary'}
                            />
                        </Box>
                    </Box>
                </Grid>


                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Employee ID"
                        name="employeeId"
                        value={formData.employeeId}
                        InputProps={{ readOnly: true }}
                        helperText="Internal HRMS ID (Read-only)"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Date of Birth"
                        name="dateOfBirth"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required
                        helperText="Age must be ≥ 18 years"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required>
                        <InputLabel>Gender</InputLabel>
                        <Select label="Gender" name="gender" value={formData.gender} onChange={handleInputChange}>
                            {genderOptions.map(option => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>Marital Status</InputLabel>
                        <Select label="Marital Status" name="maritalStatus" value={formData.maritalStatus} onChange={handleInputChange}>
                            {maritalStatusOptions.map(option => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>Blood Group</InputLabel>
                        <Select label="Blood Group" name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange}>
                            {bloodGroupOptions.map(option => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Contact Number"
                        name="primaryPhone"
                        value={formData.primaryPhone}
                        onChange={handleInputChange}
                        type="tel"
                        inputProps={{ maxLength: 10 }}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Personal Email"
                        name="personalEmail"
                        value={formData.personalEmail}
                        onChange={handleInputChange}
                        type="email"

                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Work Email"
                        name="workEmail"
                        value={formData.workEmail}
                        onChange={handleInputChange}
                        type="email"
                        required
                    />
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

                {/* Section 2: Permanent Address */}
                <Grid item xs={12}>
                    <SubHeading>Permanent Address</SubHeading>
                </Grid>

                <Grid item xs={12}>
                    <TextField fullWidth label="Address Line 1" name="permanentAddressLine1" value={formData.permanentAddressLine1} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth label="Address Line 2" name="permanentAddressLine2" value={formData.permanentAddressLine2} onChange={handleInputChange}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Autocomplete
                        fullWidth
                        options={indianStatesAndUTs}
                        value={formData.permanentState || null}
                        onChange={handleStateChange('permanent')}
                        renderInput={(params) => <TextField {...params} label="State / UT" required />}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="City" name="permanentCity" value={formData.permanentCity} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Pincode" name="permanentPincode" value={formData.permanentPincode} onChange={handleInputChange} inputProps={{ maxLength: 6 }} required helperText="6 digits (Indian PIN format)" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Country" name="permanentCountry" value={formData.permanentCountry} onChange={handleInputChange} required />
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

                {/* Section 3: Current Address & Checkbox */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                        <SubHeading>Current Address</SubHeading>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isCurrentSameAsPermanent}
                                    onChange={handleCheckboxChange}
                                    name="isCurrentSameAsPermanent"
                                    color="primary"
                                />
                            }
                            label="Same as Permanent Address"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Address Line 1"
                        name="currentAddressLine1"
                        value={formData.currentAddressLine1}
                        onChange={handleInputChange}
                        disabled={isCurrentSameAsPermanent}
                        required
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Address Line 2"
                        name="currentAddressLine2"
                        value={formData.currentAddressLine2}
                        onChange={handleInputChange}
                        disabled={isCurrentSameAsPermanent}
                       
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Autocomplete
                        fullWidth
                        options={indianStatesAndUTs}
                        value={formData.currentState || null}
                        onChange={handleStateChange('current')}
                        renderInput={(params) => <TextField {...params} label="State / UT" required />}
                        disabled={isCurrentSameAsPermanent}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="City"
                        name="currentCity"
                        value={formData.currentCity}
                        onChange={handleInputChange}
                        disabled={isCurrentSameAsPermanent}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Pincode"
                        name="currentPincode"
                        value={formData.currentPincode}
                        onChange={handleInputChange}
                        disabled={isCurrentSameAsPermanent}
                        inputProps={{ maxLength: 6 }}
                        required
                        helperText="6 digits (Indian PIN format)"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Country"
                        name="currentCountry"
                        value={formData.currentCountry}
                        onChange={handleInputChange}
                        disabled={isCurrentSameAsPermanent}
                        required
                    />
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

                {/* Section 4: Emergency Contact Details */}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <SubHeading>Emergency Contact</SubHeading>
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAnotherEmergencyContact}
                        >
                            Add Another Contact
                        </Button>
                    </Box>
                </Grid>
                {formData.emergencyContacts.map((contact, index) => (
                    <React.Fragment key={index}>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth
                                label={`Contact Name ${index + 1}`}
                                name="name"
                                value={contact.name}
                                onChange={(e) => handleEmergencyContactChange(index, e)}
                                required={index === 0}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth required>
                                <InputLabel>Relationship</InputLabel>
                                <Select label="Relationship"
                                    name="relation"
                                    value={contact.relation}
                                    onChange={(e) => handleEmergencyContactChange(index, e)}
                                >
                                    {emergencyRelationOptions.map(option => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Contact Number"
                                name="phone"
                                value={contact.phone}
                                onChange={(e) => handleEmergencyContactChange(index, e)}
                                type="tel"
                                inputProps={{ maxLength: 10 }}
                                required={index === 0}
                                helperText="10 digits (Mobile format)"
                            />
                        </Grid>
                        {index >= 0 && (
                            <Grid item xs={12} sm={1}>
                                <IconButton
                                    color="error"
                                    onClick={() => handleRemoveEmergencyContact(index)}
                                    aria-label={`Remove Contact ${index + 1}`}
                                >
                                    <Delete />
                                </IconButton>
                            </Grid>
                        )}
                    </React.Fragment>
                ))}


            </Grid>

            {/* Save Changes Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Personal & Contact Details'}</strong>
                                </Typography>
                            </Box>
                            <Button
                                type='button'
                                variant="contained"
                                color="info"
                                onClick={async (e) => {
                                    e.preventDefault();

                                    const success = await performSave();
                                    // Only return to rehire if save was successful
                                    if (success && onSaveAndReturn) {
                                        onSaveAndReturn();
                                    }
                                }}

                            >
                                Verify & Return to Rehire
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"

                        sx={{ py: 1.5, px: 4 }}
                    >
                        Save Changes
                    </Button>
                )}
            </Box>
            <ScrollToTop />
            <Modal
                open={openTokenExpirePopup}
                onClose={handleCloseTokenExpirePopup}
                aria-labelledby="token-expire-modal-title"
                aria-describedby="token-expire-modal-description"
            >
                <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PersonalContactDetails;